import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { IShopifyOrder } from '../../../../interfaces/delivery-company/shopify';
import { useSnackBar } from '../../../../contexts/snackbar';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  styled,
  Switch,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import GenericTable from '../../../common/generic-table/generic-table';
import { CustomerWithIcon } from '../../../common/styles/customer-with-icon';
import { CustomerType } from '../../../../interfaces/common/customer';
import { useImmer } from 'use-immer';
import {
  AccessTime,
  AttachMoney,
  AutoMode,
  Cached,
  Cancel,
  CheckCircle,
  Checklist,
  Clear,
  Close,
  Delete,
  DoneAll,
  HourglassBottom,
  ImportContacts,
  ImportExportRounded,
  LocalShipping,
  LocalShippingOutlined,
  LooksOne,
  MoneyOutlined,
  OpenInNew,
  PersonOutline,
  Public,
  Refresh,
  SkipNext,
  SyncLock,
  ThumbDown,
  ThumbUp,
} from '@mui/icons-material';
import { displayMoney, Money } from '../../../../utils/money';
import Decimal from 'decimal.js';
import { format } from 'date-fns';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { ShopifyIntegrationConfig } from '../../../../interfaces/common/shopify';
import Grid2 from '@mui/material/Unstable_Grid2';
import { ShopifyPackagingMode } from '../../../../enums/shopify';
import { GiCrackedGlass } from 'react-icons/gi';
import { IconWithText } from '../../../common/styles/components';
import { OrgSettingsContext } from '../../../../contexts/org-settings-context';
import { IShopifyOrderImportTask, TaskState } from '../../../../interfaces/common/async_tasks';
import { FaShopify } from 'react-icons/fa';
import { shopifyColor } from '../../../common/display-order-source/display-order-source';
import FilterChips from '../../../common/filter-chips/filter-chips';
import { PaginationContext } from '../../../../contexts/pagination-context';
import AsyncTaskList from '../../../common/async-task-list/async-task-list';

interface ImportOrdersDialogProps {
  shopifyOrders: IShopifyOrder[];
  onClose: () => void;
  integrationConfig: ShopifyIntegrationConfig;
}

const ImportOrdersDialog = ({ shopifyOrders, onClose, integrationConfig }: ImportOrdersDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [packagingMode, setPackagingMode] = useState(integrationConfig.packaging_mode);
  const [isFragile, setIsFragile] = useState(integrationConfig.default_is_fragile);
  const { showMessage } = useSnackBar();
  const { config } = useContext(AuthContext);
  const { color } = useContext(OrgSettingsContext);
  const [error, setError] = useState<string>();

  const handleImportOrders = async () => {
    try {
      setLoading(true);
      await axios.post<{ successes: number[], errors: number[] }>(`${API_ENDPOINT}/shopify/v1/orders/import/`, {
        shopify_order_numbers: Array.from(shopifyOrders).map(order => order.order_number),
        packaging_mode_override: packagingMode,
        is_fragile_override: isFragile,
      }, config);
      onClose();
    } catch {
      showMessage('Something went wrong. Try again later or contact support.', 'error');
      setError('Something went wrong. Try again later or contact support.');
    } finally {
      setLoading(false);
    }
  };

  return <CustomDialog
    open={true}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
  >
    <DialogTitle align="center">Importing {shopifyOrders.length} Shopify Orders</DialogTitle>
    <DialogContent>
      {
        loading ?
          <Box display={'flex'} gap={2} justifyContent={'center'} alignItems={'center'}>Please wait while we import the
            orders... <CircularProgress size={20} /></Box> :
          <>
            {error ? <Alert severity="error">{error}</Alert> : <>
              <Grid2 container spacing={2}>
                <Grid2 xs={12} md={6} sx={{ borderRight: '1px solid #e0e0e0' }}>
                  <Typography fontWeight={600} color={'primary'}> Packaging Mode
                  </Typography>
                  <Typography variant="body2" color={'text.secondary'}>
                    {packagingMode === ShopifyPackagingMode.AUTO ? 'Split each item in the order into separate packages.' : 'Import each order as a single package.'}
                  </Typography>
                  <Box display="flex" gap={1} mt={1}>
                    <Chip
                      onClick={() => setPackagingMode(ShopifyPackagingMode.AUTO)}
                      color={packagingMode === ShopifyPackagingMode.AUTO ? 'primary' : 'default'}
                      label={<span>Automatic</span>}
                      disabled={loading}
                      icon={<AutoMode />}
                    />

                    <Chip
                      onClick={() => setPackagingMode(ShopifyPackagingMode.ONE_PACKAGE)}
                      color={packagingMode === ShopifyPackagingMode.ONE_PACKAGE ? 'primary' : 'default'}
                      label={<span>One Package</span>}
                      disabled={loading}
                      icon={<LooksOne />}
                    />

                  </Box>
                </Grid2>

                <Grid2 xs={12} md={6} sx={{ borderLeft: '1px solid #e0e0e0', paddingLeft: 3 }}>

                  <Typography fontWeight={600} color={'primary'}> Fragile
                  </Typography>
                  <Typography variant="body2" color={'text.secondary'}>
                    {isFragile ? 'All packages will be marked as fragile.' : 'Packages will NOT be marked as fragile.'}
                  </Typography>
                  <Box display="flex" gap={1} mt={1}>
                    <Switch
                      checked={isFragile}
                      onChange={() => setIsFragile(!isFragile)}
                      disabled={loading}
                    />
                    <IconWithText> Mark as Fragile <GiCrackedGlass size={24} color={color} /></IconWithText>
                  </Box>
                </Grid2>
              </Grid2>

              <DialogActions sx={{ justifyContent: 'center', padding: 2, mt: 2 }}>
                <Button disabled={loading} onClick={handleImportOrders} variant="contained"
                        color="primary">Confirm {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}</Button>
                <Button disabled={loading} onClick={onClose} variant="outlined" color="error">Cancel</Button>
              </DialogActions>

            </>
            }
          </>


      }
    </DialogContent>


  </CustomDialog>;

};

interface ShopifyOrderImportTaskCardProps {
  status: TaskState;
}

const ShopifyOrderImportTaskCard = styled(Paper)<ShopifyOrderImportTaskCardProps>(({ theme, status }) => ({
  padding: theme.spacing(2),
  width: '100%',
  // maxWidth: '100%',
  background: theme.palette.background.paper,
  borderLeft: '6px solid',
  borderColor: theme.palette[status === 'FAILED'
    ? 'error'
    : status === 'COMPLETED'
      ? 'success'
      : 'primary'].main,
  position: 'relative',
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
}));


export enum ShopifyOrderStatus {
  PENDING_IMPORT = 'Pending Import',
  IMPORTED = 'Imported',
  FAILED = 'Failed',
  ALL_STATUSES = 'All Statuses',
}

export enum ShopifyOrderPeriod {
  ALL_TIME = 'All Time',
  LAST_24_HOURS = 'Last 24 Hours',
  LAST_7_DAYS = 'Last 7 Days',
  LAST_30_DAYS = 'Last 30 Days',
}


function ShopifyOrders({ integrationConfig }: { integrationConfig: ShopifyIntegrationConfig }) {
  const { config } = useContext(AuthContext);
  const [shopifyOrders, setShopifyOrders] = useState<IShopifyOrder[] | null>(null);
  const [count, setCount] = useState(0);
  const { showMessage } = useSnackBar();
  const [loading, setLoading] = useState(true);
  const [shopifyOrderNumberSearch, setShopifyOrderNumberSearch] = useState('');
  const [shopifyOrderNumberSearchInput, setShopifyOrderNumberSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<ShopifyOrderStatus>(ShopifyOrderStatus.ALL_STATUSES);
  const [periodFilter, setPeriodFilter] = useState<ShopifyOrderPeriod>(ShopifyOrderPeriod.ALL_TIME);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [selectedOrders, setSelectedOrders] = useImmer<Map<string, IShopifyOrder>>(new Map());

  const [importingOrders, setImportingOrders] = useState(false);
  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);
  const [tablePage, setTablePage] = useState(0);


  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const [pendingTasks, setPendingTasks] = useState<IShopifyOrderImportTask[]>([]);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await axios.get<IShopifyOrderImportTask[]>(
        `${API_ENDPOINT}/v1/jobs/shopify-order-import/`,
        config,
      );
      console.log(response.data);
      setPendingTasks(response.data);

      const stillPending = response.data.some(
        (task) => task.status !== TaskState.COMPLETED && task.status !== TaskState.FAILED,
      );

      if (!stillPending && pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      } else if (stillPending && !pollingRef.current) {
        pollingRef.current = setInterval(fetchTasks, 3000);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      showMessage('Failed to fetch tasks', 'error');
    }
  }, [config, showMessage]);


  useEffect(() => {
    fetchTasks();
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchTasks]);

  useEffect(() => {
    setTablePage(0);
  }, [paginationConfig.rowsPerPage, shopifyOrderNumberSearch, statusFilter, periodFilter]);

  const getShopifyOrders = async () => {
    setLoading(true);
    setShopifyOrders(null);
    setCount(0);
    try {
      let endpoint = `${API_ENDPOINT}/shopify/v1/orders/?page_size=${paginationConfig.rowsPerPage}&page=${tablePage + 1}`;
      if (shopifyOrderNumberSearch && shopifyOrderNumberSearch.trim() !== '') {
        endpoint += `&order_number=${shopifyOrderNumberSearch}`;
      }
      if (statusFilter !== ShopifyOrderStatus.ALL_STATUSES) {
        endpoint += `&status=${statusFilter}`;
      }
      if (periodFilter !== ShopifyOrderPeriod.ALL_TIME) {
        endpoint += `&since_hours=${periodFilter === ShopifyOrderPeriod.LAST_24_HOURS ? 24 : periodFilter === ShopifyOrderPeriod.LAST_7_DAYS ? 7 * 24 : periodFilter === ShopifyOrderPeriod.LAST_30_DAYS ? 30 * 24 : 0}`;
      }
      const response = await axios.get<{ count: number, results: IShopifyOrder[] }>(endpoint, config);
      setShopifyOrders(response.data.results);
      setCount(response.data.count);
    } catch (error) {
      console.log(error);
      showMessage('Failed to get Shopify orders', 'error');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    getShopifyOrders();
  }, [shopifyOrderNumberSearch, statusFilter, periodFilter, paginationConfig.rowsPerPage, tablePage]);

  const hasCompletedTasks = pendingTasks.some(
    task => task.status === TaskState.COMPLETED || task.status === TaskState.FAILED,
  );

  useEffect(() => {
    if (autoRefresh) {

      const interval = setInterval(() => {
        getShopifyOrders();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, getShopifyOrders]);


  return (
    <>

      <Box width="100%">
        <IconWithText centered><FaShopify size={50} color={shopifyColor} />
          <Typography variant="h4" component="h4" sx={{ textAlign: 'center', mt: 3, mb: 3 }}>Shopify Orders</Typography>
        </IconWithText>


      </Box>

      <Box width="100%">
        <Box mb={2} width="100%" gap={1} display="flex" justifyContent="flex-end">
          <Button onClick={() => setImportingOrders(true)} disabled={selectedOrders.size === 0} size="small"
                  variant="contained" color="primary"><Checklist fontSize="small" sx={{ mr: 1 }} /> Import</Button>
          <Button onClick={getShopifyOrders} size="small" variant="contained" color="info"><Refresh fontSize="small"
                                                                                                    sx={{ mr: 1 }} /> Refresh</Button>
        </Box>
      </Box>

      <AsyncTaskList
        tasks={pendingTasks}
        taskLabel="Importing Shopify Orders"
        taskIcon={<ImportExportRounded />}
        chipColor="primary"
        processingText="Processing..."
        borderPosition="left"
        renderTaskStats={(task) => (
          <Grid2 container spacing={1} width="100%">
            <Grid2 xs={12} md={4}>
              <IconWithText><ThumbUp fontSize="small"
                                     color="success" /> {task.result.success ?? 0} imported.</IconWithText>
            </Grid2>
            <Grid2 xs={12} md={4}>
              <IconWithText><ThumbDown fontSize="small"
                                       color="error" /> {task.result.failed ?? 0} failed.</IconWithText>
            </Grid2>
            <Grid2 xs={12} md={4}>
              <IconWithText><SkipNext fontSize="small" color="warning" /> {task.result.skipped ?? 0} already
                imported.</IconWithText>
            </Grid2>
          </Grid2>
        )}
      />

      {/*{pendingTasks.length > 0 && (*/}
      {/*  <Box sx={{*/}
      {/*    mb: 4,*/}
      {/*    display: 'flex',*/}
      {/*    flexDirection: 'column',*/}
      {/*    alignItems: 'center',*/}
      {/*    width: '100%',*/}
      {/*    maxWidth: '600px',*/}
      {/*    margin: 'auto',*/}
      {/*  }}>*/}
      {/*    <Stack direction="row" spacing={2} sx={{ width: '100%', mb: 2 }}>*/}
      {/*      {hasCompletedTasks && (*/}
      {/*        <Button*/}
      {/*          variant="outlined"*/}
      {/*          startIcon={<DoneAll />}*/}
      {/*          onClick={dismissAllCompleted}*/}
      {/*          sx={{ ml: 'auto' }}*/}
      {/*        >*/}
      {/*          Dismiss All Completed*/}
      {/*        </Button>*/}
      {/*      )}*/}
      {/*    </Stack>*/}

      {/*    {pendingTasks.map((task) => (*/}
      {/*      <Fade in={true} key={task.task_id}>*/}
      {/*        <ShopifyOrderImportTaskCard elevation={3} status={task.status}>*/}
      {/*          <Box display="flex" alignItems="center" justifyContent="space-between">*/}
      {/*            <Box display="flex" alignItems="center">*/}
      {/*              <Chip*/}
      {/*                icon={<ImportExportRounded />}*/}
      {/*                label="Importing Shopify Orders"*/}
      {/*                size="small"*/}
      {/*                color="primary"*/}
      {/*                sx={{ fontWeight: 600, mr: 1 }}*/}
      {/*              />*/}
      {/*              <Typography variant="caption" color="text.secondary">*/}
      {/*                Task ID: {task.task_id.slice(0, 8)}... • {new Date(task.created_at).toLocaleTimeString()}*/}
      {/*              </Typography>*/}
      {/*              {task.status === 'STARTED' && (*/}
      {/*                <Cached*/}
      {/*                  sx={{*/}
      {/*                    fontSize: 16,*/}
      {/*                    animation: 'spin 1.5s linear infinite',*/}
      {/*                    '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },*/}
      {/*                  }}*/}
      {/*                />*/}
      {/*              )}*/}
      {/*            </Box>*/}
      {/*            {(task.status == TaskState.COMPLETED || task.status == TaskState.FAILED) && <>{dismissingTasks.has(task.task_id) ?*/}
      {/*              <CircularProgress size={20} /> :*/}
      {/*              <IconButton onClick={() => dismissTask(task.task_id)} size="small"><Close /></IconButton>}</>}*/}

      {/*          </Box>*/}

      {/*          <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>*/}

      {/*            <Grid2 container spacing={1} width="100%">*/}
      {/*              <Grid2 xs={12} md={4}>*/}
      {/*                <IconWithText><ThumbUp fontSize="small"*/}
      {/*                                       color="success" /> {task.result.success ?? 0} imported.</IconWithText>*/}
      {/*              </Grid2>*/}
      {/*              <Grid2 xs={12} md={4}>*/}
      {/*                <IconWithText><ThumbDown fontSize="small"*/}
      {/*                                         color="error" /> {task.result.failed ?? 0} failed.</IconWithText>*/}
      {/*              </Grid2>*/}
      {/*              <Grid2 xs={12} md={4}>*/}
      {/*                <IconWithText><SkipNext fontSize="small" color="warning" /> {task.result.skipped ?? 0} already*/}
      {/*                  imported.</IconWithText>*/}
      {/*              </Grid2>*/}
      {/*            </Grid2>*/}
      {/*          </Box>*/}

      {/*          <Box sx={{ mb: 1 }}>*/}
      {/*            <LinearProgress*/}
      {/*              variant="determinate"*/}
      {/*              value={task.progress}*/}
      {/*              color={*/}
      {/*                task.status === 'FAILED'*/}
      {/*                  ? 'error'*/}
      {/*                  : task.status === 'COMPLETED'*/}
      {/*                    ? 'success'*/}
      {/*                    : 'primary'*/}
      {/*              }*/}
      {/*              sx={{ height: 8, borderRadius: 4 }}*/}
      {/*            />*/}
      {/*            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>*/}
      {/*              <Typography variant="caption" color="text.secondary">*/}
      {/*                {task.status === 'STARTED' ? 'Processing...' : task.status}*/}
      {/*              </Typography>*/}
      {/*              <Typography variant="caption" fontWeight="bold">*/}
      {/*                {task.progress}%*/}
      {/*              </Typography>*/}
      {/*            </Box>*/}
      {/*          </Box>*/}
      {/*        </ShopifyOrderImportTaskCard>*/}
      {/*      </Fade>*/}
      {/*    ))}*/}
      {/*  </Box>*/}
      {/*)}*/}

      <Grid2 width="100%" container spacing={1} sx={{ alignItems: 'center' }}>
        <Grid2 xs={12} md={4}>
          <FormControl fullWidth>
            <TextField
              label={'Shopify Order #'}
              value={shopifyOrderNumberSearchInput}
              onChange={(e) => setShopifyOrderNumberSearchInput(e.target.value)}
              variant="outlined"
              sx={{ maxWidth: 400, width: '100%' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setShopifyOrderNumberSearch(shopifyOrderNumberSearchInput);
                  // should unfocus the input
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {
                      shopifyOrderNumberSearchInput && <> {
                        shopifyOrderNumberSearchInput != shopifyOrderNumberSearch ?
                          <Chip label={'Hit enter to search'} size="small" color="secondary" />
                          :
                          <IconButton onClick={() => {
                            setShopifyOrderNumberSearch('');
                            setShopifyOrderNumberSearchInput('');
                          }}>
                            <Clear />
                          </IconButton>
                      }</>

                    }
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
        </Grid2>

        <Grid2 xs={12} md={4}>

          <FilterChips
            label={'Status'}
            options={[ShopifyOrderStatus.ALL_STATUSES, ShopifyOrderStatus.PENDING_IMPORT, ShopifyOrderStatus.IMPORTED, ShopifyOrderStatus.FAILED]}
            selected={[statusFilter]}
            onChange={(selected) => setStatusFilter(selected[0] as ShopifyOrderStatus)}
          />
        </Grid2>
        <Grid2 xs={12} md={4}>

          <FilterChips
            label={'Period'}
            multiple={false}
            options={[ShopifyOrderPeriod.ALL_TIME, ShopifyOrderPeriod.LAST_24_HOURS, ShopifyOrderPeriod.LAST_7_DAYS, ShopifyOrderPeriod.LAST_30_DAYS]}
            selected={[periodFilter]}
            onChange={(selected) => setPeriodFilter(selected[0] as ShopifyOrderPeriod)}
          />
        </Grid2>
      </Grid2>


      {loading ? <CircularProgress /> :
        shopifyOrders == null ?
          <Alert severity="error">Oops, something went wrong. Please try refreshing the page of contact us if the issue
            persists.</Alert> :
          <>
            <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
              <Chip label={`Selected ${selectedOrders.size}/${count}`} />
              <TablePagination
                component="div"
                onPageChange={(event, newPage) => setTablePage(newPage)}
                page={tablePage}
                count={count}
                rowsPerPage={paginationConfig.rowsPerPage}
                onRowsPerPageChange={(event) => {
                  const newRowsPerPage = parseInt(event.target.value, 10);
                  setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
                  setTablePage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
                labelRowsPerPage=""
                style={{ overflow: 'visible' }}
              />
            </Box>

            <Box display="flex" alignItems="center" justifyContent="flex-start" width="100%">
              <Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(!autoRefresh)} /> Auto Refresh every 5
              seconds
            </Box>


            <GenericTable<IShopifyOrder>
              sourceView="shopify-orders"
              items={shopifyOrders}
              isLoading={loading}
              fullWidth
              columns={[
                {
                  id: 'order_id',
                  header: <IconWithText centered><FaShopify color={shopifyColor} size={20} /> Order #</IconWithText>,
                  display_body: (order) => <a href={order.shopify_order_url} target="_blank"
                                              rel="noopener noreferrer"><Chip label={order.order_number}
                                                                              color={'secondary'} size="small"
                                                                              icon={<OpenInNew fontSize="small" />}
                                                                              sx={{ cursor: 'pointer' }} /></a>,
                },
                {
                  id: 'PT Order',
                  header: <IconWithText centered><LocalShippingOutlined color={'primary'} /> Shipping
                    Order</IconWithText>,
                  display_body: (order) => <>{order.order ?
                    <a href={`/order/${order.order}`}><Chip label={order.order} color="primary" size="small"
                                                            icon={<OpenInNew fontSize="small" />}
                                                            sx={{ cursor: 'pointer' }} /></a> :
                    <SyncLock color="primary" />}</>,
                },
                {
                  id: 'customer',
                  header: <IconWithText centered><PersonOutline color={'primary'} /> Recipient</IconWithText>,
                  display_body: (order) => {
                    console.log(order.recipient_info);
                    const { name, phone, address } = order.recipient_info;
                    return <Box>
                      {name}
                      <br />
                      {phone}
                      <br />
                      {address}
                    </Box>;
                  },
                },
                {
                  id: 'Amount',
                  header: <IconWithText centered><AttachMoney color={'primary'} />Outstanding Amount</IconWithText>,
                  display_body: (order) =>  <>{displayMoney([[order.currency, Decimal(order.total_price)]] as Money[])}</>,
                },
                {
                  id: 'Financial Status',
                  header: <IconWithText centered><AttachMoney color={'primary'} />Payment</IconWithText>,
                  display_body: (order) => <Box margin="auto"> <Chip label={order.financial_status} icon={order.financial_status == "paid" ? <CheckCircle fontSize='small' /> : undefined} color={order.financial_status == "paid" ? "success" : undefined} /></Box>,
                },
                { 
                  id: 'Last Sync',
                  header: <IconWithText centered><AccessTime color={'primary'} /> Last Sync</IconWithText>,
                  display_body: (order) => <>{order.updated_at ? format(order.updated_at, 'dd-MM-yyyy HH:mm') : 'Never'}</>,
                },
                {
                  id: 'Status', header: 'Status', display_body: (order) => {
                    if (order.validation_errors) return <Chip label={<Tooltip
                      title={<Alert sx={{ zIndex: 1000 }} severity="error">
                        <ul>{order.validation_errors.split(';').map((err, index) => <li key={index}>{err}</li>)}</ul>
                      </Alert>}><Typography
                      variant="caption">{order.validation_errors.split(';').length} errors</Typography></Tooltip>}
                                                              color="error" />;
                    if (!order.order) return <IconWithText centered><HourglassBottom fontSize="small"
                                                                                     color="info" /> Pending
                      Import</IconWithText>;
                    return <IconWithText centered><CheckCircle fontSize="small"
                                                               color="success" /> Imported</IconWithText>;
                  },
                },
              ]}
              selectedItems={selectedOrders}
              updateSelectedItems={setSelectedOrders}
              selectionEnabled
              getItemUID={(item: IShopifyOrder) => item.order_number}
            />
          </>
      }

      {
        importingOrders && <ImportOrdersDialog
          shopifyOrders={Array.from(selectedOrders.values())}
          onClose={() => {
            setImportingOrders(false);
            fetchTasks();
            setSelectedOrders(new Map());
          }
          }
          integrationConfig={integrationConfig}
        />
      }


    </>

  );
}

export default ShopifyOrders;
